import React from "react";
import { Select, type Value } from "baseui/select";
import { useNationalities, resolveNationality } from "../hooks/useNationalities";

interface Props {
  /** Currently stored value — ISO alpha-2 code OR legacy free-text. */
  value: string | null | undefined;
  onChange: (code: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** When true, render the BaseUI Select in compact size. */
  size?: "default" | "compact" | "mini";
}

/**
 * Searchable country dropdown bound to a string holding the ISO 3166-1
 * alpha-2 code. Falls back to displaying a legacy free-text value as-is
 * (with no flag) when it doesn't resolve to a known country.
 */
// Inside a drawer (Add team member) BaseUI's popover opened BEHIND it, so the list could not be opened. The project's
// rule for a Select in a drawer is 999999 !important (InvitationTypeDetailDrawer, EstatesExplorer).
const ABOVE_DRAWER = {
  Popover: { props: { overrides: { Body: { style: { zIndex: '999999 !important' } } } } },
  Dropdown: { style: { zIndex: '999999 !important' } },
};

export const NationalityPicker: React.FC<Props> = ({
  value,
  onChange,
  disabled,
  placeholder = "Select nationality",
  size = "default",
}) => {
  const { data: countries, isLoading } = useNationalities();

  const options = React.useMemo(
    () =>
      (countries ?? []).map((c) => ({
        id: c.country_code,
        label: c.country,
        flag: c.country_flag,
      })),
    [countries],
  );

  const selectedCountry = resolveNationality(value ?? undefined, countries);
  const selectedValue: Value = selectedCountry
    ? [{ id: selectedCountry.country_code, label: selectedCountry.country }]
    : [];

  // `country_flag` is a flag image URL (lipis/flag-icons), not an emoji — render
  // it as an <img>. Concatenating it as text printed the raw URL in the field.
  const renderFlagLabel = (flag: string | undefined, label: string) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      {flag && (flag.startsWith("http")
        ? <img src={flag} alt="" style={{ width: 20, height: 15, objectFit: "cover", borderRadius: 2, flexShrink: 0 }} />
        : <span>{flag}</span>)}
      {label}
    </span>
  );

  // Legacy free-text fallback shown above the empty Select so the admin still
  // sees the stored value when it isn't a known country code.
  const rawTrimmed = (value ?? "").trim();
  const showLegacyHint = !!rawTrimmed && !selectedCountry;

  return (
    <div>
      <Select
        options={options}
        value={selectedValue}
        searchable
        clearable={false}
        disabled={disabled || isLoading}
        placeholder={isLoading ? "Loading…" : placeholder}
        size={size}
        overrides={ABOVE_DRAWER}
        onChange={({ value }) => {
          const id = value[0]?.id;
          if (id != null) onChange(String(id));
        }}
        getOptionLabel={({ option }) => {
          if (!option) return "";
          const flag = (option as any).flag as string | undefined;
          return renderFlagLabel(flag, option.label as string);
        }}
        getValueLabel={({ option }) => {
          const flag = countries?.find((c) => c.country_code === option.id)?.country_flag;
          return renderFlagLabel(flag, option.label as string);
        }}
      />
      {showLegacyHint && (
        <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>
          Stored as: <span style={{ fontFamily: "monospace" }}>{rawTrimmed}</span>
        </div>
      )}
    </div>
  );
};

export default NationalityPicker;
